"use client";

import React, { useEffect, useState } from "react";
import { InstagramPost } from "@/services/queries/get-instagram-posts";
import { getInstagramPostsAction } from "@/actions/get-instagram-posts";
import SliderClientComponent from "./SliderClientComponent";
import ImageComponent from "./ImageComponent";
import { isLookSystem } from "@/utils/look-gate";

export interface InstagramPostsSliderComponentProps {
  config: {
    numberOfPosts: number;
    resourceGroupId: string;
    applicationId?: string;
    height?: string;
    width?: string;
    margin?: string;
    padding?: string;
    rounded?: string;
    size?: string;
    bgColor?: string;
    borderColor?: string;
    postRounded?: string;
    objectFit?: "contain" | "cover" | "fill" | "none" | "scale-down";
    objectPosition?: "top" | "center" | "bottom" | "left" | "right";
    aspectRatio?: string;
    cmsData?: { theme?: unknown };
    sliderConfig: {
      navigation: boolean;
      autoplay: {
        delay: number;
        disableOnInteraction: boolean;
        pauseOnMouseEnter: boolean;
      };
      spaceBetween: string;
      centeredSlides: boolean;
      speed: number;
      effect: string;
      slidesPerView?: number | "auto" | string;
    };
  };
  applicationId?: string;
  initialPosts?: InstagramPost[];
}

const InstagramPostsSliderComponent: React.FC<
  InstagramPostsSliderComponentProps
> = ({ config, applicationId, initialPosts }) => {
  const [posts, setPosts] = useState<InstagramPost[]>(initialPosts ?? []);
  const [loading, setLoading] = useState(initialPosts === undefined);
  const [error, setError] = useState<string | null>(null);

  const finalApplicationId = applicationId || config?.applicationId || "";

  useEffect(() => {
    if (initialPosts !== undefined) { setPosts(initialPosts); setLoading(false); return; }
    const fetchPosts = async () => {
      if (!finalApplicationId || !config.resourceGroupId) {
        console.warn("Missing required parameters for Instagram posts:", {
          applicationId: finalApplicationId,
          resourceGroupId: config.resourceGroupId,
        });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const result = await getInstagramPostsAction(
          finalApplicationId,
          config.resourceGroupId,
          config.numberOfPosts,
        );

        if (result.success && result.data?.data) {
          setPosts(result.data.data);
        } else {
          console.error("Failed to fetch Instagram posts:", result);
          setError(result.error || "No Instagram posts found");
        }
      } catch (err) {
        console.error("Error fetching Instagram posts:", err);
        setError("Failed to load Instagram posts");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [finalApplicationId, config.resourceGroupId, config.numberOfPosts, initialPosts]);

  // Convert posts to slider items (ImageComponent format)
  const sliderItems = posts.map((post, index) => {
    const isVideo = post.mediaType === "VIDEO";
    const imageUrl = isVideo ? post.thumbnailUrl : post.mediaUrl;

    console.log("🎬 Processing post:", {
      id: post.id,
      mediaType: post.mediaType,
      isVideo,
      mediaUrl: post.mediaUrl,
      thumbnailUrl: post.thumbnailUrl,
      finalImageUrl: imageUrl,
    });

    const imageConfig = {
      height: "100%",
      width: "100%",
      objectFit: config.objectFit || "cover",
      objectPosition: config.objectPosition || "center",
      rounded: config.postRounded || "lg",
      aspectRatio: config.aspectRatio || "auto",
      url: imageUrl || "",
      altText: post.caption || `Instagram post ${index + 1}`,
      ...(isLookSystem(config.cmsData?.theme) ? { lookSystem: true } : {}),
    };

    return <ImageComponent key={post.id} config={imageConfig} />;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">
            Loading Instagram posts...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600">No Instagram posts available</p>
        </div>
      </div>
    );
  }

  // Create slider config exactly like regular SliderComponent
  const sliderConfig = {
    cmsData: { applicationId: finalApplicationId } as any,
    app: { id: "instagram-slider" } as any,
    items: sliderItems,
    config: {
      navigation: config.sliderConfig.navigation,
      slidesPerView: config.sliderConfig.slidesPerView ?? "auto",
      spaceBetween: config.sliderConfig.spaceBetween ?? "1px",
      centeredSlides: config.sliderConfig.centeredSlides ?? false,
      speed: config.sliderConfig.speed ?? 500,
      effect: config.sliderConfig.effect ?? "slide",
      autoplay:
        config.sliderConfig.autoplay.delay > 0
          ? config.sliderConfig.autoplay
          : undefined,
    },
  };

  return (
    <div
      className="w-full"
      style={
        {
          height: config.height || "auto",
          width: config.width || "100%",
        } as React.CSSProperties
      }
    >
      <SliderClientComponent config={sliderConfig} items={sliderItems} />
    </div>
  );
};

export default InstagramPostsSliderComponent;
